import { Target, Swords, Lock, Coins, Globe, Flag, ShieldCheck, TrendingDown } from "lucide-react";

export function Strike5Card({ strike5 }) {
  const s = strike5 || {};
  const rated = s.has_rating;
  const prov = !rated || s.is_provisional;
  return (
    <div data-testid="strike5-rating-card" className="rounded-2xl bg-[#0f0f0f] border border-[#2b2512] p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-fuchsia-300 font-mono font-bold mb-4">
        <Target className="w-4 h-4" /> Strike 5 · Individual
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Rating</div>
          <div data-testid="strike5-rating" className="font-heading text-3xl font-black text-fuchsia-300 tabular-nums leading-none">
            {rated ? Math.round(s.rating) : 1500}
          </div>
          {prov && <div data-testid="strike5-progress" className="text-[10px] font-mono text-slate-500 mt-1">{s.official_progress ? `${s.official_progress.results}/${s.official_progress.results_required} · RD ${s.official_progress.rd ?? "—"}` : "provisional"}</div>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 flex items-center gap-1"><Globe className="w-3 h-3" /> World</div>
          <div className="font-heading text-3xl font-black text-slate-50 tabular-nums leading-none">
            {!prov && s.world_rank ? `#${s.world_rank.position}` : "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1 flex items-center gap-1"><Flag className="w-3 h-3" /> Greece</div>
          <div className="font-heading text-3xl font-black text-slate-50 tabular-nums leading-none">
            {!prov && s.national_rank ? `#${s.national_rank.position}` : "—"}
          </div>
        </div>
      </div>
      <div className="mt-3 text-[11px] font-mono text-slate-500">Rounds played: {s.matches_count || 0}</div>
    </div>
  );
}

export function ChallengeComingSoon() {
  return (
    <div data-testid="challenge-coming-soon" className="hidden" />
  );
}

export function DisciplineCard({ block, label, accent = "text-[#F0C72A]", testid }) {
  const s = block || {};
  const rated = s.has_rating;
  const prov = !rated || s.is_provisional;
  return (
    <div data-testid={testid} className="rounded-2xl bg-[#0f0f0f] border border-[#2b2512] p-5">
      <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-mono font-bold mb-4 ${accent}`}>
        <Target className="w-4 h-4" /> {label} · Individual
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Rating</div>
          <div data-testid={`${testid}-rating`} className={`font-heading text-3xl font-black tabular-nums leading-none ${accent}`}>{rated ? Math.round(s.rating) : 1500}</div>
          {prov && <div data-testid={`${testid}-progress`} className="text-[10px] font-mono text-slate-500 mt-1">{s.official_progress ? `${s.official_progress.results}/${s.official_progress.results_required} · RD ${s.official_progress.rd ?? "—"}` : "provisional"}</div>}
        </div>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">World</div><div className="font-heading text-3xl font-black text-slate-50 tabular-nums leading-none">{!prov && s.world_rank ? `#${s.world_rank.position}` : "—"}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Greece</div><div className="font-heading text-3xl font-black text-slate-50 tabular-nums leading-none">{!prov && s.national_rank ? `#${s.national_rank.position}` : "—"}</div></div>
      </div>
    </div>
  );
}

export function OverallCard({ overall }) {
  const o = overall || {};
  return (
    <div data-testid="overall-rating-card" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-4">
        <Target className="w-4 h-4" /> Overall Rating
      </div>
      {o.eligible ? (
        <div className="grid grid-cols-3 gap-3">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Rating</div><div data-testid="overall-rating" className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none">{Math.round(o.rating)}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">World</div><div className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none">{o.world_rank ? `#${o.world_rank.position}` : "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Greece</div><div className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none">{o.national_rank ? `#${o.national_rank.position}` : "—"}</div></div>
        </div>
      ) : (
        <div data-testid="overall-not-eligible" className="text-sm text-slate-400">
          <Lock className="w-4 h-4 inline mr-1 text-slate-500" /> Not eligible yet — unlock Overall by becoming <span className="text-slate-200">OFFICIAL ranked in at least one discipline</span> with ≥25% of your rating activity (ArenaBook · Challenge · Strike 5). Specialists qualify.
        </div>
      )}
      {o.eligible && o.weights && (
        <div data-testid="overall-weights" className="mt-4 pt-4 border-t border-[#2b2512]">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">Contribution weights</div>
            {o.protection_applied && (
              <span data-testid="overall-protected-badge" className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-wider bg-emerald-950/60 text-emerald-300 border border-emerald-500/40">
                Activation-protected
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(o.weights).map(([d, w]) => (
              <span key={d} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#070707] border border-[#2b2512] text-xs">
                <span className="text-slate-300 font-mono">{d}</span>
                <span className="text-[#E5B842] font-heading font-bold tabular-nums">{(w * 100).toFixed(1)}%</span>
              </span>
            ))}
          </div>
          {o.protection_applied && o.activation_protection_baseline != null && (
            <div data-testid="overall-protection-note" className="mt-2 text-[11px] font-mono text-slate-500">
              Activation-protected: at the event a new discipline became eligible, your Overall was held at the pre-activation baseline <span className="text-emerald-300">{Math.round(o.activation_protection_baseline)}</span> vs computed <span className="text-slate-400">{Math.round(o.activation_product)}</span>. v1.1 — protection applies only at activation; the Overall moves freely afterwards (no permanent floor).
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function OverallShowcaseTimeline({ showcase }) {
  const s = showcase || {};
  const stages = s.timeline || [];
  if (!stages.length) return null;
  const label = { PRE_ACTIVATION: "Pre-activation", ACTIVATION: "Activation event",
                  POST_ACTIVATION_RECOMPUTE: "Post-activation recompute" };
  return (
    <div data-testid="overall-showcase-timeline" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-5 mt-3">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold">
          <ShieldCheck className="w-4 h-4" /> Overall · Activation-Event Protection
        </div>
        <span className="font-mono text-[9px] text-slate-500 border border-[#2b2512] rounded px-1.5 py-0.5">{s.downside_protection_version}</span>
      </div>
      {s.note && <div className="text-[11px] text-slate-400 mb-4 leading-relaxed">{s.note}</div>}
      <ol className="relative border-l border-[#2b2512] ml-1 space-y-4">
        {stages.map((st) => {
          const protectedStage = st.protection_applied === true;
          const declined = st.stage === "POST_ACTIVATION_RECOMPUTE";
          return (
            <li key={st.stage} data-testid={`overall-showcase-stage-${st.stage}`} className="ml-4">
              <span className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 ${protectedStage ? "bg-emerald-400 border-emerald-300" : declined ? "bg-amber-400 border-amber-300" : "bg-slate-500 border-slate-400"}`} />
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-slate-100 text-sm">{label[st.stage] || st.stage}</span>
                {protectedStage && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase bg-emerald-950/60 text-emerald-300 border border-emerald-500/40">
                    <ShieldCheck className="w-3 h-3" /> Protected
                  </span>
                )}
                {declined && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-mono font-bold uppercase bg-amber-950/60 text-amber-300 border border-amber-500/40">
                    <TrendingDown className="w-3 h-3" /> Free to decline
                  </span>
                )}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-heading text-3xl font-black text-[#E5B842] tabular-nums leading-none" data-testid={`overall-showcase-published-${st.stage}`}>{Math.round(st.published)}</span>
                <span className="text-[10px] font-mono text-slate-500">published</span>
              </div>
              <div className="mt-1 text-[11px] font-mono text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>eligible: {(st.eligible || []).join(" + ") || "—"}</span>
                {st.computed != null && <span>computed: {Math.round(st.computed)}</span>}
                {st.activation_protection_baseline != null && <span>baseline: <span className="text-emerald-300">{Math.round(st.activation_protection_baseline)}</span></span>}
                {st.activation_product != null && <span>product: {Math.round(st.activation_product)}</span>}
              </div>
              {st.note && <div className="mt-1 text-[10px] text-slate-500 italic">{st.note}</div>}
            </li>
          );
        })}
      </ol>
      {s.current_overall != null && (
        <div data-testid="overall-showcase-current" className="mt-4 pt-3 border-t border-[#2b2512] text-[11px] font-mono text-slate-400">
          Current published Overall: <span className="text-[#E5B842] font-bold">{Math.round(s.current_overall)}</span> — settled at the computed value, confirming protection is an activation event only, not a permanent high-water mark.
        </div>
      )}
    </div>
  );
}


export function WalletWidget({ wallet }) {
  const w = wallet || {};
  const txns = w.transactions || [];
  const tone = (t) => (t.amount >= 0 ? "text-emerald-400" : "text-red-400");
  return (
    <div data-testid="wallet-widget" className="rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold">
          <Coins className="w-4 h-4" /> Virtual Points Wallet
        </div>
      </div>
      <div className="mt-3 font-heading text-5xl font-black text-[#E5B842] tabular-nums" data-testid="wallet-balance">
        {(w.balance ?? 0).toLocaleString()} <span className="text-base text-slate-500 font-mono">VP</span>
      </div>
      <div data-testid="wallet-disclaimer" className="mt-1 text-[11px] font-mono text-slate-500">
        {w.disclaimer || "Virtual points have no monetary value — never purchasable, never cashable."}
      </div>
      <div className="mt-4 pt-4 border-t border-[#2b2512]">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">Recent activity</div>
        {txns.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No transactions yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="wallet-transactions">
            {txns.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-slate-400 truncate">
                  <span className="font-mono text-[10px] uppercase text-slate-500 mr-1.5">{t.type}</span>{t.note}
                </span>
                <span className={`font-heading font-bold tabular-nums shrink-0 ${tone(t)}`}>
                  {t.amount >= 0 ? "+" : ""}{t.amount.toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
